"use client";

import { isServer } from "@decibeltrade/web-core/utils";
import { useEffectEvent, useState } from "react";

import { useIsomorphicLayoutEffect as useLayoutEffect } from "./use-isomorphic-layout-effect";

/**
 * A hook that accepts a CSS media query string and returns a boolean indicating if the query matches.
 * When ran on the server, it returns null.
 */
export function useMediaQuery(query: string): boolean | null {
  const getMatches = () => {
    if (isServer()) {
      return null;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(() => getMatches());

  const handleChange = useEffectEvent(() => {
    setMatches(getMatches());
  });

  useLayoutEffect(() => {
    // Trigger on first client-side load
    handleChange();

    const matchMedia = window.matchMedia(query);
    matchMedia.addEventListener("change", handleChange);
    return () => {
      matchMedia.removeEventListener("change", handleChange);
    };
  }, [query]);

  return matches;
}
